/**
 * Logo inspector plumbing (AB#20790): which vector production places for each
 * custom logo, the designer's manual uploads beside it, and the stored choice.
 * Pure decisions live in utils/productionFile/logoSources.ts; this file owns
 * the HTTP calls and the per-session manual-vector cache.
 */
import { http } from '@/interceptors/axiosRequestInterceptor';
import type { CustomLogo, LogoVectorSource, VectorizedLogoInfo } from '@/models/CustomLogo';
import { buildVectorizeLogoUrl, productionStorageUrl } from '@/utils/productionFile/endpoints';
import {
  buildLogoUrlResolver,
  manualVectorBaseKey,
  placedLogoKey,
  type ManualVectorInfo,
} from '@/utils/productionFile/logoSources';

/** base key -> probe; one request per logo per page load. */
const manualInfoCache = new Map<string, Promise<ManualVectorInfo | null>>();

export function invalidateManualVectorInfo(baseKey: string): void {
  manualInfoCache.delete(baseKey);
}

/** `GET admin/manual-vectorized-logo-info?path=` — null when the probe fails. */
export function fetchManualVectorInfo(baseKey: string): Promise<ManualVectorInfo | null> {
  let pending = manualInfoCache.get(baseKey);
  if (!pending) {
    pending = http
      .get('manual-vectorized-logo-info', { params: { path: baseKey } })
      .then((response) => {
        const result = response.data?.result ?? response.data;
        if (!result || typeof result.has_manual !== 'boolean') return null;
        return {
          has_manual: result.has_manual,
          extension: result.extension ?? null,
          meta: result.meta ?? null,
        } as ManualVectorInfo;
      })
      .catch(() => null);
    manualInfoCache.set(baseKey, pending);
  }
  return pending;
}

/** Manual-vector state for every logo, keyed by the PLACED key (what the composite carries). */
export async function probeManualVectors(
  logos: CustomLogo[]
): Promise<Map<string, ManualVectorInfo | null>> {
  const out = new Map<string, ManualVectorInfo | null>();
  await Promise.all(
    logos.map(async (logo) => {
      const placed = placedLogoKey(logo);
      const base = manualVectorBaseKey(logo);
      if (!placed || !base) return;
      out.set(placed, await fetchManualVectorInfo(base));
    })
  );
  return out;
}

/**
 * The `resolveUrl` hook for inlineCustomLogoVectors for one product: probes the
 * manual vectors once, then applies the stored/default source per logo.
 * Warning codes for choices that could not be honoured go to `notes`.
 */
export async function buildProductionLogoResolver(
  logos: CustomLogo[] | undefined,
  notes?: string[]
): Promise<((s3Key: string) => string | null | undefined) | undefined> {
  if (!logos?.length) return undefined;
  const manual = await probeManualVectors(logos);
  return buildLogoUrlResolver(
    logos,
    { storageUrl: productionStorageUrl(), vectorizeUrl: buildVectorizeLogoUrl },
    manual,
    notes
  );
}

export interface LogoAddress {
  order_item_id: number | string;
  factory_product_id: number | string;
  logo_index: number;
}

/** Persist the choice on the order (`POST admin/logo-vector-source`). */
export async function saveLogoVectorSource(
  address: LogoAddress,
  source: LogoVectorSource
): Promise<VectorizedLogoInfo | null> {
  const response = await http.post('logo-vector-source', { ...address, source });
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Could not save the production source');
  }
  return (response.data.result?.vectorized_logo_info as VectorizedLogoInfo) ?? null;
}

/** Designer upload next to the logo (`POST admin/upload-manual-vectorized-logo`). */
export async function uploadManualVector(
  baseKey: string,
  file: File
): Promise<{ vectorized_path: string; meta: ManualVectorInfo['meta'] }> {
  const form = new FormData();
  form.append('file', file);
  form.append('path', baseKey);
  const response = await http.post('upload-manual-vectorized-logo', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Upload failed');
  }
  invalidateManualVectorInfo(baseKey);
  return response.data.result;
}

/**
 * Run the auto trace for a logo that has none stored (`POST admin/vectorize_logo`,
 * the existing button's endpoint — it writes the result onto the order).
 * Costs a vectorizer credit in production; the inspector only offers it when
 * the order carries no trace.
 */
export async function requestAutoVectorization(
  logo: CustomLogo,
  address: LogoAddress
): Promise<VectorizedLogoInfo | null> {
  const response = await http.post('vectorize_logo', {
    logo_url: logo.original_logo || logo.original_logo_url || logo.url,
    order_item_id: address.order_item_id,
    factory_product_id: address.factory_product_id,
    logo_index: address.logo_index,
    vectorizer_params: { 'output.file_format': 'svg' },
  });
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Vectorization failed');
  }
  return (response.data.result?.vectorized_logo_info as VectorizedLogoInfo) ?? null;
}
